import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import BottomFixed from "@/components/bottom-fixed";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, Eye, Loader, Save } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { catchError } from "@/lib/errors";
import { handleApiError } from "@/utils/errors";
import { dilemmasRepo, questionsRepo, answersRepo } from "@/lib/repositories";
import { queryClient } from "@/lib/query-client";
import { ROUTES } from "@/constants/routes";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import * as z from "zod";
import type { ApiDilemma } from "../../types";

interface Props {
  dilemma: ApiDilemma;
}

interface OriginalDialogState {
  title: string;
  body: React.ReactNode;
}

function SectionHeader({
  title,
  onShowOriginal,
}: {
  title: string;
  onShowOriginal: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between mb-4">
      <h3 className="text-base font-semibold">{title}</h3>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="text-muted-foreground"
        onClick={onShowOriginal}
      >
        <Eye className="size-4" />
        {t("modules.dilemmas.form.show_original")}
      </Button>
    </div>
  );
}

const DilemmaTranslationForm = ({ dilemma }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const targetLang = dilemma.source_language === "fr" ? "en" : "fr";
  const existing = dilemma.translations?.[targetLang];
  const question = dilemma.questions[0];

  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogState, setDialogState] = useState<OriginalDialogState | null>(
    null,
  );

  const schema = z.object({
    name: z.string().min(1, t("modules.dilemmas.errors.description_required")),
    description: z
      .string()
      .min(1, t("modules.dilemmas.errors.description_required")),
    questionPayload: z
      .string()
      .min(1, t("modules.dilemmas.errors.question_required")),
    answers: z.array(
      z.object({
        payload: z
          .string()
          .min(1, t("modules.dilemmas.errors.answer_required")),
        comment: z
          .string()
          .min(1, t("modules.dilemmas.errors.feedback_required")),
      }),
    ),
    synthesis: z
      .string()
      .min(1, t("modules.dilemmas.errors.synthesis_required")),
    review_comment: z.string(),
  });

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: existing?.name ?? "",
      description: existing?.description ?? "",
      questionPayload: question?.translations?.[targetLang]?.payload ?? "",
      answers:
        question?.answers.map((a) => ({
          payload: a.translations?.[targetLang]?.payload ?? "",
          comment: a.translations?.[targetLang]?.comment ?? "",
        })) ?? [],
      synthesis: existing?.synthesis ?? "",
      review_comment: existing?.review_comment ?? "",
    },
  });

  function showOriginal(title: string, body: React.ReactNode) {
    setDialogState({ title, body });
    setDialogOpen(true);
  }

  async function onSubmit(data: z.infer<typeof schema>) {
    setIsLoading(true);

    const promises: Promise<unknown>[] = [
      dilemmasRepo.patch(dilemma.id, {
        translations: {
          [targetLang]: {
            name: data.name,
            description: data.description,
            synthesis: data.synthesis,
            review_comment: data.review_comment,
          },
        },
      }),
    ];

    if (question) {
      promises.push(
        questionsRepo.patch(question.id, {
          translations: { [targetLang]: { payload: data.questionPayload } },
        }),
      );
      question.answers.forEach((answer, i) => {
        promises.push(
          answersRepo.patch(answer.id, {
            translations: {
              [targetLang]: {
                payload: data.answers[i].payload,
                comment: data.answers[i].comment,
              },
            },
          }),
        );
      });
    }

    const [error] = await catchError(Promise.all(promises));
    if (error) {
      toast.error(handleApiError(error).message);
      setIsLoading(false);
      return;
    }

    await queryClient.invalidateQueries({ queryKey: ["dilemma", dilemma.id] });
    toast.success(t("messages.dilemma_translation_update_success"));
    navigate(ROUTES.DILEMMAS);
    setIsLoading(false);
  }

  const langLabel = targetLang === "en" ? "English" : "Français";

  return (
    <>
      <div className="flex flex-col gap-6 mb-24 bg-white w-full p-6 border rounded-md">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-semibold">
            {t("modules.dilemmas.form.translating_to", { lang: langLabel })}
          </h2>
          <p className="text-sm text-muted-foreground">{dilemma.reference}</p>
        </div>

        <form id="translation-form" onSubmit={form.handleSubmit(onSubmit)}>
          {/* Section 1 — Main info */}
          <SectionHeader
            title={t("modules.dilemmas.form.translation_section_main")}
            onShowOriginal={() =>
              showOriginal(
                t("modules.dilemmas.form.translation_section_main"),
                <div className="flex flex-col gap-4 text-sm">
                  <div>
                    <p className="font-semibold text-muted-foreground mb-1">
                      {t("modules.dilemmas.keys.name")}
                    </p>
                    <p>{dilemma.name}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-muted-foreground mb-1">
                      {t("modules.dilemmas.form.description")}
                    </p>
                    <p className="whitespace-pre-wrap">{dilemma.description}</p>
                  </div>
                </div>,
              )
            }
          />
          <FieldGroup className="gap-4">
            <Controller
              name="name"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel required>
                    {t("modules.dilemmas.keys.name")}
                  </FieldLabel>
                  <Input {...field} aria-invalid={fieldState.invalid} />
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
            <Controller
              name="description"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel required>
                    {t("modules.dilemmas.form.description")}
                  </FieldLabel>
                  <Textarea
                    {...field}
                    rows={4}
                    aria-invalid={fieldState.invalid}
                  />
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
          </FieldGroup>

          <div className="h-8"></div>

          {/* Section 2 — Q&A */}
          <SectionHeader
            title={t("modules.dilemmas.form.translation_section_qa")}
            onShowOriginal={() =>
              showOriginal(
                t("modules.dilemmas.form.translation_section_qa"),
                <div className="flex flex-col gap-4 text-sm">
                  <div>
                    <p className="font-semibold text-muted-foreground mb-1">
                      {t("modules.dilemmas.form.the_question")}
                    </p>
                    <p>{question?.payload}</p>
                  </div>
                  {question?.answers.map((a) => (
                    <div
                      key={a.id}
                      className="border rounded-md p-3 flex flex-col gap-2"
                    >
                      <p className="font-semibold uppercase text-xs text-muted-foreground">
                        {t("modules.dilemmas.form.answer_label", {
                          label: a.label.toUpperCase(),
                        })}
                      </p>
                      <div>
                        <p className="text-muted-foreground text-xs mb-0.5">
                          {t("modules.dilemmas.form.answer_text")}
                        </p>
                        <p>{a.payload}</p>
                      </div>
                      <div>
                        <p className="text-muted-foreground text-xs mb-0.5">
                          {t("modules.dilemmas.form.answer_comment")}
                        </p>
                        <p>{a.comment}</p>
                      </div>
                    </div>
                  ))}
                </div>,
              )
            }
          />
          <FieldGroup className="gap-4">
            <Controller
              name="questionPayload"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel required>
                    {t("modules.dilemmas.form.the_question")}
                  </FieldLabel>
                  <Input {...field} aria-invalid={fieldState.invalid} />
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
            {question?.answers.map((answer, i) => (
              <div
                key={answer.id}
                className="border rounded-md p-4 flex flex-col gap-3"
              >
                <p className="text-sm font-semibold uppercase text-muted-foreground">
                  {t("modules.dilemmas.form.answer_label", {
                    label: answer.label.toUpperCase(),
                  })}
                </p>
                <Controller
                  name={`answers.${i}.payload`}
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel required>
                        {t("modules.dilemmas.form.answer_text")}
                      </FieldLabel>
                      <Input {...field} aria-invalid={fieldState.invalid} />
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  )}
                />
                <Controller
                  name={`answers.${i}.comment`}
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <FieldLabel required>
                        {t("modules.dilemmas.form.answer_comment")}
                      </FieldLabel>
                      <Textarea
                        {...field}
                        rows={2}
                        aria-invalid={fieldState.invalid}
                      />
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  )}
                />
              </div>
            ))}
          </FieldGroup>

          <div className="h-8"></div>

          {/* Section 3 — Synthesis */}
          <SectionHeader
            title={t("modules.dilemmas.form.translation_section_synthesis")}
            onShowOriginal={() =>
              showOriginal(
                t("modules.dilemmas.form.translation_section_synthesis"),
                <div className="flex flex-col gap-4 text-sm">
                  <div>
                    <p className="font-semibold text-muted-foreground mb-1">
                      {t("modules.dilemmas.form.general_comment")}
                    </p>
                    <p className="whitespace-pre-wrap">{dilemma.synthesis}</p>
                  </div>
                  <div>
                    <p className="font-semibold text-muted-foreground mb-1">
                      {t("modules.dilemmas.form.review_comment_title")}
                    </p>
                    <p className="whitespace-pre-wrap">
                      {dilemma.review_comment || "—"}
                    </p>
                  </div>
                  {dilemma.documentation_links.length > 0 && (
                    <div>
                      <p className="font-semibold text-muted-foreground mb-1">
                        {t("modules.dilemmas.form.add_external_link")}
                      </p>
                      {dilemma.documentation_links.map((l) => (
                        <p key={l}>
                          {dilemma.documentation_link_titles[l] && (
                            <span className="block font-medium">
                              {dilemma.documentation_link_titles[l]}
                            </span>
                          )}
                          <span className="text-blue-600 underline break-all">
                            {l}
                          </span>
                        </p>
                      ))}
                    </div>
                  )}
                </div>,
              )
            }
          />
          <FieldGroup className="gap-4">
            <Controller
              name="synthesis"
              control={form.control}
              render={({ field, fieldState }) => (
                <Field data-invalid={fieldState.invalid}>
                  <FieldLabel required>
                    {t("modules.dilemmas.form.general_comment")}
                  </FieldLabel>
                  <Textarea
                    {...field}
                    rows={5}
                    aria-invalid={fieldState.invalid}
                  />
                  {fieldState.invalid && (
                    <FieldError errors={[fieldState.error]} />
                  )}
                </Field>
              )}
            />
            <Controller
              name="review_comment"
              control={form.control}
              render={({ field }) => (
                <Field>
                  <FieldLabel>
                    {t("modules.dilemmas.form.review_comment_title")}
                  </FieldLabel>
                  <Textarea {...field} rows={3} />
                </Field>
              )}
            />
          </FieldGroup>
        </form>
      </div>

      {/* Bottom bar */}
      <BottomFixed className="bg-white">
        <div className="flex items-center justify-between gap-3">
          <Button
            variant="ghost"
            className="border-primary border text-primary"
            type="button"
            onClick={() => navigate(ROUTES.DILEMMAS)}
          >
            <ArrowLeft />
            {t("keys.previous")}
          </Button>
          <Button disabled={isLoading} type="submit" form="translation-form">
            {isLoading ? (
              <Loader className="animate-spin" />
            ) : (
              <Save className="size-4" />
            )}
            {t("keys.save")}
          </Button>
        </div>
      </BottomFixed>

      {/* Original text dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {dialogState?.title ??
                t("modules.dilemmas.form.original_content")}
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2">{dialogState?.body}</div>
          <div className="mt-4 flex justify-end">
            <DialogClose asChild>
              <Button variant="outline">{t("keys.cancel")}</Button>
            </DialogClose>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DilemmaTranslationForm;
